import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { Close, Warning, CheckCircle } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sv';
import {
  NOTICE_PERIOD_MONTHS,
  earliestMoveOutDate,
  latestMoveOutDate,
  recommendedMoveOutDate,
  validateMoveOutDate,
} from '../utils/termination';

interface TerminateRentalModalProps {
  open: boolean;
  onClose: () => void;
  tenantName: string;
  apartmentNumber: string;
  address: string;
  contractEndDate: string;
  onConfirm: (moveOutDate: string, reason?: string) => Promise<void>;
}

const TerminateRentalModal: React.FC<TerminateRentalModalProps> = ({
  open,
  onClose,
  tenantName,
  apartmentNumber,
  address,
  contractEndDate,
  onConfirm
}) => {
  const [moveOutDate, setMoveOutDate] = useState<Dayjs | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Tre månader är den normala uppsägningstiden, men den spärrar inte: kommer
  // parterna överens om ett tidigare utflytt ska det gå. Kalendern begränsas
  // bara av idag och avtalets slutdag. Samma regel körs på servern, se
  // src/utils/termination.ts.
  const today = dayjs().format('YYYY-MM-DD');
  const earliest = earliestMoveOutDate(today);
  const latest = latestMoveOutDate(contractEndDate);
  const recommended = recommendedMoveOutDate(today, contractEndDate);
  const minDate = dayjs(earliest);
  const maxDate = latest ? dayjs(latest) : undefined;

  const handleConfirm = async () => {
    if (!moveOutDate) {
      setError('Välj ett utflyttningsdatum');
      return;
    }

    const dateError = validateMoveOutDate(moveOutDate.format('YYYY-MM-DD'), today, contractEndDate);
    if (dateError) {
      setError(dateError);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onConfirm(moveOutDate.format('YYYY-MM-DD'), reason || undefined);
      setSuccess(true);
    } catch (err) {
      console.error('Error initiating termination:', err);
      // Serverns egen förklaring är mer användbar än en generisk text.
      setError(err instanceof Error && err.message ? err.message : 'Ett fel uppstod. Försök igen.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
      setSuccess(false);
      setMoveOutDate(null);
      setReason('');
    }
  };

  if (success) {
    return (
      <Dialog open={open} className="fixed z-50 inset-0 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-md mx-auto p-8 text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
            <CheckCircle sx={{ fontSize: 48, color: '#22c55e' }} />
          </div>

          <h2 className="text-xl font-bold text-[#222222] font-['Poppins'] mb-2">
            Uppsägning initierad
          </h2>

          <p className="text-sm text-[#626262]">
            Hyresgästen kommer att få ett mail med en länk för att signera uppsägningen.
          </p>

          <button
            onClick={handleClose}
            className="mt-6 bg-[#62B8E9] text-white font-bold py-2.5 px-6 rounded-lg hover:bg-[#4FA8D5] transition-colors text-sm"
          >
            Stäng
          </button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="fixed z-50 inset-0 flex items-center justify-center p-4"
      PaperProps={{
        style: { backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible' }
      }}
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-lg mx-auto max-h-[90vh] flex flex-col">
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
          onClick={handleClose}
          disabled={loading}
          aria-label="Stäng"
        >
          <Close className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Warning sx={{ fontSize: 24, color: '#F59E0B' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
                Säg upp uthyrning
              </h2>
              <p className="text-sm text-[#626262]">
                {address} {apartmentNumber && `Lgh ${apartmentNumber}`}
              </p>
            </div>
          </div>

          {/* Info om uppsägningstid */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900 font-semibold mb-1">
              Normalt {NOTICE_PERIOD_MONTHS} månaders uppsägningstid
            </p>
            <p className="text-sm text-blue-800">
              {`Med normal uppsägningstid blir utflyttningen ${recommended}. Kommer du och hyresgästen överens om ett tidigare datum går det bra — välj det i så fall här.`}
              {latest ? ` Avtalet löper ut ${latest}.` : ''}
              {' '}Hyresgästen får ett mejl med en länk för att signera uppsägningen.
            </p>
          </div>

          {/* Aktuell information */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#626262]">Hyresgäst:</span>
              <span className="font-semibold text-[#222222]">{tenantName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#626262]">Avtalets slutdatum:</span>
              <span className="font-semibold text-[#222222]">
                {dayjs(contractEndDate).format('YYYY-MM-DD')}
              </span>
            </div>
          </div>

          {/* Datumväljare */}
          <div>
            <label className="block text-sm font-semibold text-[#222222] mb-2">
              Utflyttningsdatum *
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sv">
              <DatePicker
                value={moveOutDate}
                onChange={(date) => setMoveOutDate(date)}
                minDate={minDate}
                maxDate={maxDate}
                format="YYYY-MM-DD"
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    placeholder: 'Välj utflyttningsdatum',
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* Anledning (valfri) */}
          <div>
            <label className="block text-sm font-semibold text-[#222222] mb-2">
              Anledning (valfritt)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ange anledning till uppsägning..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62B8E9] resize-none"
              rows={3}
              disabled={loading}
            />
          </div>

          {/* Action buttons */}
          <div className="border-t border-[#E2EAF0] pt-4 mt-4">
            <div className="flex gap-2">
              <button
                onClick={handleConfirm}
                disabled={loading || !moveOutDate}
                className="flex-1 bg-orange-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? 'Initierar...' : 'Initiera uppsägning'}
              </button>
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 border border-gray-300 text-gray-700 font-bold py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TerminateRentalModal;
